import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { WarehouseIcon, WH_ICONS, WH_ICON_LABEL, DEFAULT_WH_COLOR } from "@/components/WarehouseBadge";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Field } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const EMPTY = { name: "", type: "secondaire", address: "", color: DEFAULT_WH_COLOR, icon: "warehouse" };

export default function Warehouses() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/warehouses").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (edit.id) await api.put(`/warehouses/${edit.id}`, edit); else await api.post("/warehouses", edit);
      toast.success("Dépôt enregistré"); setEdit(null); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (w) => {
    if (!window.confirm(`Supprimer « ${w.name} » ?`)) return;
    try { await api.delete(`/warehouses/${w.id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="warehouses-page">
      <PageHeader title="Dépôts" subtitle="Dépôts principaux et secondaires">
        <Button onClick={() => setEdit({ ...EMPTY })} className="bg-[#059669] hover:bg-[#047857]" data-testid="warehouse-add-btn"><Plus size={16} /> Nouveau dépôt</Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="warehouses-grid">
        {items.map((w) => (
          <div key={w.id} className="stat-card rise flex flex-col gap-3 border-l-4" style={{ borderLeftColor: w.color || DEFAULT_WH_COLOR }} data-testid={`warehouse-card-${w.name}`}>
            <div className="flex items-start justify-between">
              <WarehouseIcon warehouse={w} size={18} />
              <Badge variant={w.type === "principal" ? "default" : "secondary"}>{w.type}</Badge>
            </div>
            <div><p className="font-display text-xl font-semibold">{w.name}</p><p className="text-sm text-muted-foreground">{w.address || "—"}</p></div>
            <div className="flex gap-1"><Button variant="ghost" size="sm" onClick={() => setEdit({ ...w })} data-testid={`warehouse-edit-${w.name}`}><Pencil size={14} /> Modifier</Button><Button variant="ghost" size="sm" className="text-destructive" onClick={() => remove(w)} data-testid={`warehouse-delete-${w.name}`}><Trash2 size={14} /> Supprimer</Button></div>
          </div>
        ))}
      </div>
      <Dialog open={!!edit} onOpenChange={() => setEdit(null)}>
        <DialogContent data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle className="font-display">{edit?.id ? "Modifier le dépôt" : "Nouveau dépôt"}</DialogTitle></DialogHeader>
          {edit && <div className="space-y-3">
            <Field label="Nom"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="warehouse-form-name" /></Field>
            <Field label="Type"><Select value={edit.type} onValueChange={(v) => setEdit({ ...edit, type: v })}><SelectTrigger data-testid="warehouse-form-type"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="principal">Principal</SelectItem><SelectItem value="secondaire">Secondaire</SelectItem></SelectContent></Select></Field>
            <Field label="Adresse"><Input value={edit.address} onChange={(e) => setEdit({ ...edit, address: e.target.value })} data-testid="warehouse-form-address" /></Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Couleur"><div className="flex items-center gap-2"><input type="color" value={edit.color || DEFAULT_WH_COLOR} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="h-10 w-14 cursor-pointer rounded-md border bg-transparent" data-testid="warehouse-form-color" /><Input value={edit.color || DEFAULT_WH_COLOR} onChange={(e) => setEdit({ ...edit, color: e.target.value })} className="font-mono" data-testid="warehouse-form-color-text" /></div></Field>
              <Field label="Icône"><Select value={edit.icon || "warehouse"} onValueChange={(v) => setEdit({ ...edit, icon: v })}><SelectTrigger data-testid="warehouse-form-icon"><SelectValue /></SelectTrigger><SelectContent>{Object.keys(WH_ICONS).map((k) => { const I = WH_ICONS[k]; return <SelectItem key={k} value={k}><span className="inline-flex items-center gap-2"><I size={14} /> {WH_ICON_LABEL[k]}</span></SelectItem>; })}</SelectContent></Select></Field>
            </div>
            <div className="flex items-center gap-3 rounded-md bg-muted/60 p-3"><WarehouseIcon warehouse={edit} size={16} /><span className="font-semibold">{edit.name || "Aperçu du dépôt"}</span></div>
            <Button className="w-full" onClick={save} disabled={!edit.name} data-testid="warehouse-form-submit">Enregistrer</Button>
          </div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
